import { PMDonutChartCard } from "@pimo/pimo-components/src/lib/pm-components";

import { APP_ROUTES } from "../../../constants";
import {
  getDropdownCount,
  getSemaphoreStatusOptions,
} from "../../../utils/dropdown-options";
import { isFieldEnabled } from "../../field-enabled";
import { DashboardCardContext } from "../context";
import { statusReportingLevel } from "../status-reporting-level";

/**
 * How the program's projects are distributed over the statuses — the plain
 * ones, or the semaphore ones when status is reported per category.
 *
 * As with the status card, these were two components registering the same
 * `componentId: "statusOfProjectsDonutChartComponent"` and hiding each other
 * with complementary `mapVisibility` calls. They are one component that reads
 * its data from a different place depending on the setting; see
 * ./status.ts for why the id has to stay the one that is already there.
 */
export function addStatusOfProjectsDonutChartCard(ctx: DashboardCardContext) {
  const { view, app, getCardCopy } = ctx;

  const statusOfProjectsDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "statusOfProjectsDonutChartComponent",
      title: "",
      description: "",
      labels: ["donutChart", "chart", "status"],
    },
  });

  statusOfProjectsDonutChartComponent.mapLayoutProps(
    ({ language, settings }) => {
      return {
        componentId: "statusOfProjectsDonutChartComponent",
        labels: [
          "donutChart",
          "chart",
          statusReportingLevel(settings) === "category"
            ? "semaphoreStatus"
            : "status",
        ],
        title: getCardCopy(language, "statusOfProjects").title,
        description: getCardCopy(language, "statusOfProjects").description,
      };
    }
  );

  statusOfProjectsDonutChartComponent.mapState(
    ({ dashboard, settings, language }) => {
      const perCategory = statusReportingLevel(settings) === "category";
      const statusCounts =
        (perCategory
          ? dashboard?._calculated?.semaphoreStatusCounts
          : dashboard?._calculated?.projectStatusCounts) ?? {};
      const entries = perCategory
        ? getSemaphoreStatusOptions(settings?.configuration)
        : (settings?.configuration?.statusDefinition ?? []);
      const data = entries.map((entry) => {
        return {
          label: entry?.name ?? "",
          value: (entry && getDropdownCount(statusCounts, entry)) ?? 0,
          color: entry?.color ?? "",
        };
      });

      const handleStatusFilter = (status?: string) => {
        app.patchAppState({
          filterData: {
            searchQueryFilter: [],
            statusFilter: status ? [status] : [],
            completionFilter: [],
            categoryFilter: [],
            updateStatusFilter: [],
          },
        });
        app.navigate(APP_ROUTES.overview);
      };

      return {
        title: getCardCopy(language, "statusOfProjects").title,
        icon: "DonutLarge",
        data,
        onCardClick: () => handleStatusFilter(),
        onSegmentClick: (segmentIndex: number) =>
          handleStatusFilter(data[segmentIndex]?.label),
      };
    }
  );

  statusOfProjectsDonutChartComponent.mapVisibility(({ settings }) =>
    isFieldEnabled(settings, "status", "itemReporting")
  );
}
